import { useMemo, useState } from 'react';
import {
  GRID_COLS,
  TOTAL_CELLS,
  type TileLayout,
} from '@/pages/spatial-span-spick/gameConfig';
import type { RoundFeedback } from '@/pages/spatial-span-spick/hooks/useGameState';

interface TileGridProps {
  /** 게임판 실측 크기에서 계산된 타일 한 변 길이(px) */
  tileSize: number;
  layout: TileLayout;
  /** 현재 문제의 제시 순서 (점등 타일에 순서 번호를 표시하기 위함) */
  sequence: number[];
  showingIndex: number;
  isShowing: boolean;
  onTilePress: (index: number) => void;
  isInputEnabled: boolean;
  userSequence: number[];
  feedback: RoundFeedback;
  /** Stage 성공 Replay 중 점등되는 타일 인덱스 */
  replayIndex: number;
  /** Replay 중에는 사용자 입력 강조를 숨기고 Replay 점등만 보여준다 */
  isReplaying: boolean;
}

// 스픽(Spick) 버전 타일 그리드
// - 참고 이미지 4종(기본/점등/클릭/오답) 스타일을 CSS 클래스로 구현해 이미지 없이 코드로 표현한다.
//   기본: 연한 하늘색, 점등: 선명한 파랑+Glow, 클릭: 진한 네이비, 오답: 코랄 레드+흰색 X
// - 점등 타일에는 순서 번호를 함께 보여준다.
export default function TileGrid({
  tileSize,
  layout,
  sequence,
  showingIndex,
  isShowing,
  onTilePress,
  isInputEnabled,
  userSequence,
  feedback,
  replayIndex,
  isReplaying,
}: TileGridProps) {
  // Touch Down 즉시 눌린 느낌을 주기 위한 로컬 상태 (판정보다 먼저 발생)
  const [pressedIndex, setPressedIndex] = useState(-1);

  // 셀 인덱스(row * cols + col) → 타일 인덱스 매핑
  const tileByCell = useMemo(() => {
    const map = new Map<number, number>();
    layout.forEach((cell, index) => {
      map.set(cell.row * GRID_COLS + cell.col, index);
    });
    return map;
  }, [layout]);

  return (
    <div className="w-full h-full">
      {/* 보이지 않는 4x7 반응형 Grid (화면 높이에 따라 여백 축소) */}
      <div className="w-full h-full grid grid-cols-4 grid-rows-7 p-[var(--sh-board-padding)]">
        {Array.from({ length: TOTAL_CELLS }, (_, cellIndex) => {
          const tileIndex = tileByCell.get(cellIndex);
          if (tileIndex === undefined) {
            return <div key={cellIndex} />;
          }

          const isShowingTile = showingIndex === tileIndex;
          const isReplayTile = isReplaying && replayIndex === tileIndex;
          const isLit = isShowingTile || isReplayTile;
          const isPressed = !isReplaying && userSequence.includes(tileIndex);
          const isWrong = feedback === 'wrong' && isPressed;
          const isTouchDown = pressedIndex === tileIndex;

          // 점등되는 타일에는 순서 번호를 함께 보여준다
          const stepNumber = isLit ? sequence.indexOf(tileIndex) + 1 : 0;

          return (
            <div key={cellIndex} className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => {
                  if (isInputEnabled) {
                    onTilePress(tileIndex);
                  }
                }}
                onPointerDown={() => {
                  if (isInputEnabled && !isShowingTile) {
                    setPressedIndex(tileIndex);
                  }
                }}
                onPointerUp={() => setPressedIndex(-1)}
                onPointerLeave={() => setPressedIndex(-1)}
                onPointerCancel={() => setPressedIndex(-1)}
                disabled={!isInputEnabled || isShowingTile}
                aria-label={`타일 ${tileIndex + 1}`}
                style={{ width: `${tileSize}px`, height: `${tileSize}px` }}
                className={`
                  sh-tile shrink-0
                  flex items-center justify-center select-none
                  ${isReplayTile
                    ? 'sh-tile-lit z-10 animate-tile-replay'
                    : isShowingTile
                      ? 'sh-tile-lit z-10 animate-tile-show'
                      : isWrong
                        ? 'sh-tile-wrong animate-tile-shake'
                        : isPressed
                          ? 'sh-tile-click animate-tile-select'
                          : isTouchDown
                            ? 'sh-tile-click scale-95'
                            : `sh-tile-default${isInputEnabled ? ' active:scale-95 cursor-pointer' : ''}`}
                `}
              >
                {isLit && (
                  <span className="text-white font-bold text-[22px] leading-none tabular-nums">
                    {stepNumber}
                  </span>
                )}
                {isWrong && (
                  <span className="relative block w-[42%] h-[42%]" aria-hidden="true">
                    <span className="absolute top-1/2 left-0 w-full h-[4px] -translate-y-1/2 rotate-45 rounded-full bg-white" />
                    <span className="absolute top-1/2 left-0 w-full h-[4px] -translate-y-1/2 -rotate-45 rounded-full bg-white" />
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}